/**
 * The front door. Four ways in — guided setup (essentials or everything),
 * manual entry, or a previously saved plan file — plus "continue" when a plan
 * is remembered on this device.
 */
import { useRef } from 'react';

interface Props {
  savedAvailable: boolean;
  /** What "continue" resumes: a finished plan, or a wizard mid-setup. */
  resumeKind?: 'plan' | 'wizard';
  resumeDetail?: string;
  onContinueSaved: () => void;
  onSimpleWizard: () => void;
  onAdvancedWizard: () => void;
  onManual: () => void;
  onLoadFile: (file: File) => void;
}

export function Launcher({
  savedAvailable, resumeKind = 'plan', resumeDetail,
  onContinueSaved, onSimpleWizard, onAdvancedWizard, onManual, onLoadFile,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div className="app launcher">
      <header className="topbar" style={{ justifyContent: 'center' }}>
        <div className="brand" style={{ textAlign: 'center' }}>
          Retirement Planner
          <span className="sub">private · runs entirely in your browser</span>
        </div>
      </header>

      {savedAvailable && (
        <button className="card launcher-continue" onClick={onContinueSaved}>
          <strong>
            {resumeKind === 'wizard' ? 'Continue setup where you left off' : 'Continue where you left off'}
          </strong>
          <span>
            {resumeKind === 'wizard'
              ? `Your answers are saved on this device — the wizard reopens on the step you stopped at. ${resumeDetail ?? ''}`.trim()
              : 'A plan is remembered on this device — pick up right where you stopped.'}
          </span>
        </button>
      )}

      <div className="launcher-grid">
        <button className="card launcher-door" onClick={onSimpleWizard}>
          <strong>Quick setup</strong>
          <span>
            About 5 minutes of plain questions — ages, savings, spending, Social Security —
            for a solid first picture of your retirement.
          </span>
          <em>Best for most people, first time through</em>
        </button>

        <button className="card launcher-door" onClick={onAdvancedWizard}>
          <strong>Full setup</strong>
          <span>
            Everything in quick setup, plus pensions, rentals, inheritances, moving states,
            long-term care, and tax strategies — it only asks about what applies to you.
          </span>
          <em>10–20 minutes, the complete walkthrough</em>
        </button>

        <button className="card launcher-door" onClick={onManual}>
          <strong>Enter it myself</strong>
          <span>Skip the questions and type directly into the planner. Every field starts at zero.</span>
          <em>Best if you know your way around</em>
        </button>

        <button className="card launcher-door" onClick={() => fileRef.current?.click()}>
          <strong>Open a saved plan</strong>
          <span>Load a plan file you downloaded earlier — yours or one someone shared with you.</span>
          <em>.json file from "Download plan file"</em>
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json"
          hidden
          onChange={(e) => e.target.files?.[0] && onLoadFile(e.target.files[0])}
        />
      </div>

      <p className="disclaimer" style={{ textAlign: 'center', maxWidth: 620, margin: '0 auto' }}>
        Nothing you enter leaves your device. There is no account, no server, and no tracking —
        your numbers live in this browser tab until you save a file or copy a link.
      </p>
    </div>
  );
}
